import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  useWindowDimensions,
  LayoutChangeEvent,
  Image,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import Animated, {
  FadeInDown,
  FadeInUp,
  FadeIn,
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  Easing,
  runOnJS,
} from 'react-native-reanimated';
import Head from 'expo-router/head';
import {
  useFonts,
  Outfit_400Regular,
  Outfit_500Medium,
  Outfit_700Bold,
} from '@expo-google-fonts/outfit';
import { LOGO_WORDMARK_WHITE, LOGO_WORDMARK_LIGHT } from '@/lib/logo';
import {
  Menu,
  X,
  Zap,
  Target,
  FileSearch,
  Users,
  TrendingUp,
  BarChart3,
  Inbox,
  LineChart,
  Sun,
  Moon,
} from 'lucide-react-native';
import { WaitlistForm } from '@/components/WaitlistForm';
import { BetaBubbleButton } from '@/components/beta/BetaBubbleButton';
import { BetaLoginModal } from '@/components/beta/BetaLoginModal';
import { WaitlistModal } from '@/components/beta/WaitlistModal';
import { useTrackScreen } from '@/lib/analytics/use-track-screen';
import { supabase } from '@/lib/supabase';
import { reportError } from '@/lib/errors';

// ============================================================================
// Constants
// ============================================================================

type Theme = typeof DARK;

const DARK = {
  bgDeep: '#060e1a',
  bgPrimary: '#0a1628',
  bgElevated: '#0f1f38',
  bgLight: '#132744',
  teal: '#4fd1c5',
  tealDim: 'rgba(79,209,197,0.15)',
  indigo: '#818cf8',
  indigoDim: 'rgba(129,140,248,0.15)',
  white: '#FFFFFF',
  text1: 'rgba(255,255,255,0.85)',
  text2: 'rgba(255,255,255,0.6)',
  text3: 'rgba(255,255,255,0.38)',
  surface: 'rgba(255,255,255,0.05)',
  border: 'rgba(255,255,255,0.08)',
  borderLight: 'rgba(255,255,255,0.15)',
  navBg: 'rgba(6,14,26,0.88)',
  heroGrad: ['#060e1a', '#0a1628', '#0f1f38'] as readonly string[],
  ctaTextOnAccent: '#060e1a',
};

const LIGHT = {
  bgDeep: '#ececf0',
  bgPrimary: '#ffffff',
  bgElevated: '#f5f5f7',
  bgLight: '#e5e5ea',
  teal: '#0d7d71',
  tealDim: 'rgba(13,125,113,0.12)',
  indigo: '#0066cc',
  indigoDim: 'rgba(0,102,204,0.10)',
  white: '#1d1d1f',
  text1: '#1d1d1f',
  text2: '#48484a',
  text3: '#8e8e93',
  surface: '#ffffff',
  border: 'rgba(0,0,0,0.10)',
  borderLight: 'rgba(0,0,0,0.18)',
  navBg: 'rgba(255,255,255,0.85)',
  heroGrad: ['#ececf0', '#f5f5f7', '#ffffff'] as readonly string[],
  ctaTextOnAccent: '#ffffff',
};

// Static ref for components outside main — default dark
const C = DARK;

// ============================================================================
// Alternating Tagline
// ============================================================================

const VALUE_PROPS = [
  { audience: 'Startups', line: 'Raise with confidence, not guesswork.', color: 'teal' as const },
  { audience: 'Investors', line: 'Better deals. Less noise. Faster decisions.', color: 'indigo' as const },
  { audience: 'Startups', line: 'Walk into every meeting investor-ready.', color: 'teal' as const },
  { audience: 'Investors', line: 'Spend time on deals, not on screening.', color: 'indigo' as const },
  { audience: 'Startups', line: 'Raise faster. Preserve your runway.', color: 'teal' as const },
  { audience: 'Investors', line: 'Pre-scored deals. Focus on what matters.', color: 'indigo' as const },
];

function ValueCarousel({ c, fontFamily, isMobile }: { c: typeof DARK; fontFamily: string | undefined; isMobile: boolean }) {
  const [idx, setIdx] = useState(0);
  const opacity = useSharedValue(1);
  const translateY = useSharedValue(0);

  useEffect(() => {
    const interval = setInterval(() => {
      // Slide up + fade out
      opacity.value = withTiming(0, { duration: 300, easing: Easing.in(Easing.ease) });
      translateY.value = withTiming(-12, { duration: 300, easing: Easing.in(Easing.ease) }, () => {
        runOnJS(setIdx)((prev: number) => (prev + 1) % VALUE_PROPS.length);
        // Reset below, slide up + fade in
        translateY.value = 12;
        translateY.value = withTiming(0, { duration: 350, easing: Easing.out(Easing.ease) });
        opacity.value = withTiming(1, { duration: 350, easing: Easing.out(Easing.ease) });
      });
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  const animStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }],
  }));

  const prop = VALUE_PROPS[idx];
  const accentColor = prop.color === 'teal' ? c.teal : c.indigo;
  const dimColor = prop.color === 'teal' ? c.tealDim : c.indigoDim;

  return (
    <View style={{ alignItems: 'center', minHeight: isMobile ? 48 : 40 }}>
      <Animated.View style={[{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }, animStyle]}>
        <View style={{ backgroundColor: dimColor, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 5 }}>
          <Text style={{ color: accentColor, fontSize: isMobile ? 12 : 14, fontFamily, fontWeight: '700', letterSpacing: 0.5 }}>
            {prop.audience}
          </Text>
        </View>
        <Text style={{ color: c.text1, fontSize: isMobile ? 15 : 18, fontFamily }}>
          {prop.line}
        </Text>
      </Animated.View>
    </View>
  );
}

/** Animated counter that counts up from 0 to target */
function AnimatedStat({ value, label, color, fontFamily, delay = 0 }: {
  value: string; label: string; color: string; fontFamily: string | undefined; delay?: number;
}) {
  const progress = useSharedValue(0);
  const numericPart = parseInt(value.replace(/[^0-9]/g, ''), 10);
  const prefix = value.match(/^[^0-9]*/)?.[0] || '';
  const suffix = value.match(/[^0-9]*$/)?.[0] || '';
  const isNumeric = !isNaN(numericPart) && numericPart > 0;

  const [display, setDisplay] = useState(isNumeric ? `${prefix}0${suffix}` : value);

  useEffect(() => {
    if (!isNumeric) return;
    const timeout = setTimeout(() => {
      progress.value = withTiming(1, { duration: 1800, easing: Easing.out(Easing.cubic) });
    }, delay + 800);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!isNumeric) return;
    const interval = setInterval(() => {
      const current = Math.round(progress.value * numericPart);
      setDisplay(`${prefix}${current}${suffix}`);
      if (current >= numericPart) clearInterval(interval);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <View style={{ alignItems: 'center' }}>
      <Text style={{ color, fontSize: 26, fontFamily, fontWeight: '700' }}>{display}</Text>
      <Text style={{ color: 'rgba(128,128,128,0.7)', fontSize: 11, fontFamily, marginTop: 4, letterSpacing: 0.3 }}>{label}</Text>
    </View>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function BetaLandingPage() {
  // All hooks must be called unconditionally before any early return (React rules of hooks)
  useTrackScreen('BetaLanding');

  const router = useRouter();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const scrollRef = useRef<ScrollView>(null);
  const waitlistY = useRef(0);

  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState<'dark' | 'light'>('dark');
  const c = mode === 'dark' ? DARK : LIGHT;
  const isDark = mode === 'dark';

  // Cards: white + shadow on gray sections, light gray on white sections (dark: transparent surface + border)
  const cardOnGray = useMemo(() => isDark
    ? { backgroundColor: c.surface, borderWidth: 1, borderColor: c.border }
    : { backgroundColor: '#ffffff', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.08, shadowRadius: 16, elevation: 3, borderWidth: 0.5, borderColor: 'rgba(0,0,0,0.06)' },
  [isDark, c.surface, c.border]);
  const cardOnWhite = useMemo(() => isDark
    ? { backgroundColor: c.surface, borderWidth: 1, borderColor: c.border }
    : { backgroundColor: '#f5f5f7', borderWidth: 0.5, borderColor: 'rgba(0,0,0,0.06)' },
  [isDark, c.surface, c.border]);
  const [waitlistCount, setWaitlistCount] = useState<number | null>(null);
  const [showCount, setShowCount] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [verifyBanner, setVerifyBanner] = useState<'success' | 'already' | 'invalid' | null>(null);

  const [fontsLoaded] = useFonts({
    Outfit_400Regular,
    Outfit_500Medium,
    Outfit_700Bold,
  });

  const f = (weight: 400 | 500 | 700) => {
    if (!fontsLoaded) return undefined;
    return { 400: 'Outfit_400Regular', 500: 'Outfit_500Medium', 700: 'Outfit_700Bold' }[weight];
  };

  // Floating orb animations
  const orb1 = useSharedValue(0);
  const orb2 = useSharedValue(0);
  useEffect(() => {
    orb1.value = withRepeat(withTiming(20, { duration: 6000, easing: Easing.inOut(Easing.ease) }), -1, true);
    orb2.value = withRepeat(withTiming(-15, { duration: 8000, easing: Easing.inOut(Easing.ease) }), -1, true);
  }, []);
  const orbStyle1 = useAnimatedStyle(() => ({ transform: [{ translateY: orb1.value }] }));
  const orbStyle2 = useAnimatedStyle(() => ({ transform: [{ translateY: orb2.value }] }));

  // JSON-LD
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'beta-landing-jsonld';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Inverio',
      applicationCategory: 'FinanceApplication',
      operatingSystem: 'Web',
      description: 'AI-powered investment readiness scoring for startups and investors',
      url: 'https://inverio.ai',
      offers: { '@type': 'Offer', availability: 'https://schema.org/PreOrder', price: '0', priceCurrency: 'CHF' },
    });
    document.head.appendChild(script);
    return () => { document.getElementById('beta-landing-jsonld')?.remove(); };
  }, []);

  // Check for email verification result in URL
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const verify = params.get('verify');
    if (verify === 'success' || verify === 'already' || verify === 'invalid') {
      setVerifyBanner(verify);
      // Clean up URL without reloading
      params.delete('verify');
      const clean = params.toString();
      window.history.replaceState({}, '', clean ? `?${clean}` : window.location.pathname);
    }
  }, []);

  // Waitlist count
  useEffect(() => {
    supabase.functions.invoke('waitlist-count', { body: {} })
      .then(({ data }) => {
        if (data) { setWaitlistCount(data.count); setShowCount(data.showCount); }
      })
      .catch((err) => reportError(err, { context: 'BetaLandingPage.waitlistCount', silent: true }));
  }, []);

  const aboutY = useRef(0);
  const whyUsY = useRef(0);
  const howItWorksY = useRef(0);

  const scrollToSection = useCallback((section: 'about' | 'waitlist' | 'why' | 'how') => {
    const map = { about: aboutY, waitlist: waitlistY, why: whyUsY, how: howItWorksY };
    const y = map[section].current;
    scrollRef.current?.scrollTo({ y, animated: true });
  }, []);

  const scrollToWaitlist = useCallback(() => {
    scrollRef.current?.scrollTo({ y: waitlistY.current, animated: true });
  }, []);

  const onWaitlistLayout = useCallback((e: LayoutChangeEvent) => {
    waitlistY.current = e.nativeEvent.layout.y;
  }, []);

  // Early return for non-web platforms (after all hooks)
  if (Platform.OS !== 'web') return null;

  const px = isMobile ? 20 : 40;

  // Shared section heading
  const SectionHead = ({ label, title, sub, delay = 100 }: { label?: string; title: string; sub?: string; delay?: number }) => (
    <Animated.View entering={FadeInUp.delay(delay).duration(600)} style={{ alignItems: 'center', marginBottom: isMobile ? 40 : 56 }}>
      {label && (
        <Text style={{ color: c.teal, fontSize: 12, fontFamily: f(500), letterSpacing: 1.5, marginBottom: 12, textTransform: 'uppercase' as any }}>
          {label}
        </Text>
      )}
      <Text style={{ color: c.white, fontSize: isMobile ? 28 : 40, fontFamily: f(700), textAlign: 'center', lineHeight: isMobile ? 36 : 50 }}>
        {title}
      </Text>
      {sub && (
        <Text style={{ color: c.text2, fontSize: 16, fontFamily: f(400), textAlign: 'center', lineHeight: 24, marginTop: 12, maxWidth: 560 }}>
          {sub}
        </Text>
      )}
    </Animated.View>
  );

  return (
    <View style={{ flex: 1, backgroundColor: c.bgDeep }}>
      <Head>
        <title>Inverio — AI Investment Readiness Scoring | Beta</title>
        <meta name="description" content="Join the Inverio beta. AI scores your startup across 15+ criteria. Fix gaps. Get matched. Raise faster." />
        <link rel="canonical" href="https://inverio.ai/" />
        <meta property="og:title" content="Inverio — Know Your Number Before Investors Do" />
        <meta property="og:description" content="AI-powered investment readiness scoring. Join the beta waitlist." />
        <meta property="og:url" content="https://inverio.ai/" />
      </Head>

      {/* ── Nav ── */}
      <View style={{
        position: 'sticky' as any,
        top: 0,
        zIndex: 100,
        backgroundColor: c.navBg,
        backdropFilter: 'blur(12px)',
        borderBottomWidth: 1,
        borderBottomColor: c.border,
      } as any}>
        <View style={{
          flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
          maxWidth: 1200, width: '100%', alignSelf: 'center', paddingHorizontal: px, paddingVertical: 14,
        }}>
          <Image
            source={isDark ? LOGO_WORDMARK_WHITE : LOGO_WORDMARK_LIGHT}
            style={{ width: 160, height: 52 }}
            resizeMode="contain"
          />

          {!isMobile && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 32 }}>
              <Pressable onPress={() => scrollToSection('why')}>
                <Text style={{ color: c.text2, fontSize: 14, fontFamily: f(500) }}>Why Us</Text>
              </Pressable>
              <Pressable onPress={() => scrollToSection('how')}>
                <Text style={{ color: c.text2, fontSize: 14, fontFamily: f(500) }}>How It Works</Text>
              </Pressable>
              <Pressable onPress={() => scrollToSection('about')}>
                <Text style={{ color: c.text2, fontSize: 14, fontFamily: f(500) }}>About</Text>
              </Pressable>
              <Pressable onPress={() => scrollToSection('waitlist')}>
                <Text style={{ color: c.text2, fontSize: 14, fontFamily: f(500) }}>Join Beta</Text>
              </Pressable>
              <Pressable
                onPress={() => setMode(isDark ? 'light' : 'dark')}
                style={{
                  width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center',
                  backgroundColor: isDark ? c.surface : c.bgElevated, borderWidth: 1, borderColor: c.border,
                }}
              >
                {isDark ? <Sun size={16} color={c.text2} /> : <Moon size={16} color={c.text2} />}
              </Pressable>
              <Pressable onPress={() => setShowLoginModal(true)} style={{
                borderWidth: 1, borderColor: c.borderLight, borderRadius: 8, paddingHorizontal: 16, paddingVertical: 8,
              }}>
                <Text style={{ color: c.white, fontSize: 14, fontFamily: f(500) }}>Sign In</Text>
              </Pressable>
            </View>
          )}

          {isMobile && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Pressable
                onPress={() => setMode(isDark ? 'light' : 'dark')}
                style={{
                  width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center',
                  backgroundColor: isDark ? c.surface : c.bgElevated, borderWidth: 1, borderColor: c.border,
                }}
              >
                {isDark ? <Sun size={16} color={c.text2} /> : <Moon size={16} color={c.text2} />}
              </Pressable>
              <Pressable onPress={() => setMenuOpen(!menuOpen)}>
                {menuOpen ? <X size={24} color={c.white} /> : <Menu size={24} color={c.white} />}
              </Pressable>
            </View>
          )}
        </View>

        {isMobile && menuOpen && (
          <Animated.View entering={FadeIn.duration(200)} style={{ paddingHorizontal: px, paddingBottom: 16, gap: 16 }}>
            <Pressable onPress={() => { setMenuOpen(false); scrollToSection('why'); }}>
              <Text style={{ color: c.text2, fontSize: 16, fontFamily: f(500) }}>Why Us</Text>
            </Pressable>
            <Pressable onPress={() => { setMenuOpen(false); scrollToSection('how'); }}>
              <Text style={{ color: c.text2, fontSize: 16, fontFamily: f(500) }}>How It Works</Text>
            </Pressable>
            <Pressable onPress={() => { setMenuOpen(false); scrollToSection('about'); }}>
              <Text style={{ color: c.text2, fontSize: 16, fontFamily: f(500) }}>About</Text>
            </Pressable>
            <Pressable onPress={() => { setMenuOpen(false); scrollToSection('waitlist'); }}>
              <Text style={{ color: c.text2, fontSize: 16, fontFamily: f(500) }}>Join Beta</Text>
            </Pressable>
            <Pressable onPress={() => { setMenuOpen(false); setShowLoginModal(true); }}>
              <Text style={{ color: c.teal, fontSize: 16, fontFamily: f(500) }}>Sign In</Text>
            </Pressable>
          </Animated.View>
        )}
      </View>

      <ScrollView ref={scrollRef} style={{ flex: 1 }}>

        {/* ══════════════════════════════════════════════════════════════════
            HERO — gradient bg
        ══════════════════════════════════════════════════════════════════ */}
        <LinearGradient
          colors={[c.heroGrad[0], c.heroGrad[1], c.heroGrad[2]]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ paddingHorizontal: px, paddingTop: isMobile ? 56 : 100, paddingBottom: isMobile ? 56 : 100, alignItems: 'center', overflow: 'hidden' }}
        >
          <Animated.View style={[{ position: 'absolute', top: -100, right: -80, width: 300, height: 300, borderRadius: 150, backgroundColor: isDark ? 'rgba(79,209,197,0.06)' : 'rgba(13,125,113,0.08)' }, orbStyle1]} />
          <Animated.View style={[{ position: 'absolute', bottom: -60, left: -100, width: 250, height: 250, borderRadius: 125, backgroundColor: isDark ? 'rgba(129,140,248,0.04)' : 'rgba(0,102,204,0.06)' }, orbStyle2]} />

          <View style={{ maxWidth: 860, alignItems: 'center', gap: isMobile ? 18 : 24 }}>
            {/* Badge */}
            <Animated.View entering={FadeInDown.delay(100).duration(600)}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderColor: c.teal, borderRadius: 20, paddingHorizontal: 14, paddingVertical: 6 }}>
                <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: c.teal }} />
                <Text style={{ color: c.teal, fontSize: 12, fontFamily: f(500), letterSpacing: 1 }}>
                  INVITE-ONLY BETA
                </Text>
              </View>
            </Animated.View>

            {/* Main headline */}
            <Animated.View entering={FadeInDown.delay(250).duration(700)} style={{ alignItems: 'center' }}>
              <Text style={{ color: c.white, fontSize: isMobile ? 32 : 52, fontFamily: f(700), textAlign: 'center', lineHeight: isMobile ? 40 : 62 }}>
                AI-Powered Investment Readiness{'\n'}for{' '}
                <Text style={{ color: c.teal }}>Startups</Text> &{' '}
                <Text style={{ color: c.indigo }}>Investors</Text>
              </Text>
            </Animated.View>

            {/* Sub-headline — what it actually does */}
            <Animated.View entering={FadeInDown.delay(400).duration(600)}>
              <Text style={{ color: c.text2, fontSize: isMobile ? 15 : 18, fontFamily: f(400), textAlign: 'center', lineHeight: isMobile ? 22 : 28, maxWidth: 620 }}>
                Inverio scores startup readiness, surfaces gaps, and matches founders with the right investors — so both sides move faster with less guesswork.
              </Text>
            </Animated.View>

            {/* Value prop carousel */}
            <Animated.View entering={FadeInDown.delay(550).duration(600)}>
              <ValueCarousel c={c} fontFamily={f(500)} isMobile={isMobile} />
            </Animated.View>

            {/* CTAs */}
            <Animated.View entering={FadeInDown.delay(650).duration(600)} style={{ flexDirection: isMobile ? 'column' : 'row', gap: 12, marginTop: 4, width: isMobile ? '100%' : undefined }}>
              <Pressable onPress={scrollToWaitlist} style={{ backgroundColor: c.teal, borderRadius: 12, paddingHorizontal: 32, paddingVertical: 16, alignItems: 'center', flexDirection: 'row', gap: 8 }}>
                <Text style={{ color: c.ctaTextOnAccent, fontFamily: f(700), fontSize: 16 }}>Join the Waitlist</Text>
                <Text style={{ color: c.ctaTextOnAccent, fontSize: 16 }}>→</Text>
              </Pressable>
              <Pressable onPress={() => setShowLoginModal(true)} style={{ borderWidth: 1, borderColor: c.borderLight, borderRadius: 12, paddingHorizontal: 28, paddingVertical: 16, alignItems: 'center' }}>
                <Text style={{ color: c.white, fontFamily: f(500), fontSize: 16 }}>I Have an Invite</Text>
              </Pressable>
            </Animated.View>

            {/* Proof bar */}
            <Animated.View entering={FadeInDown.delay(800).duration(600)} style={{
              flexDirection: 'row', gap: isMobile ? 20 : 40, marginTop: isMobile ? 16 : 32,
              flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center',
            }}>
              <AnimatedStat value="3" label="Scores" color={c.teal} fontFamily={f(700)} delay={0} />
              <View style={{ width: 1, height: 28, backgroundColor: c.border }} />
              <AnimatedStat value="15+" label="Dimensions" color={c.teal} fontFamily={f(700)} delay={150} />
              <View style={{ width: 1, height: 28, backgroundColor: c.border }} />
              <AnimatedStat value="400+" label="Sub-Criteria" color={c.teal} fontFamily={f(700)} delay={300} />
              <View style={{ width: 1, height: 28, backgroundColor: c.border }} />
              <AnimatedStat value="4" label="Valuation Methods" color={c.teal} fontFamily={f(700)} delay={450} />
              <View style={{ width: 1, height: 28, backgroundColor: c.border }} />
              <View style={{ alignItems: 'center' }}>
                <Text style={{ color: c.teal, fontSize: 26, fontFamily: f(700) }}>CH</Text>
                <Text style={{ color: c.text3, fontSize: 11, fontFamily: f(400), marginTop: 4, letterSpacing: 0.3 }}>Swiss-Built</Text>
              </View>
            </Animated.View>
          </View>
        </LinearGradient>

        {/* ══════════════════════════════════════════════════════════════════
            BENEFITS — elevated bg (lighter)
        ══════════════════════════════════════════════════════════════════ */}
        <View onLayout={(e) => { whyUsY.current = e.nativeEvent.layout.y; }} style={{ paddingHorizontal: px, paddingVertical: isMobile ? 60 : 100, backgroundColor: c.bgElevated, alignItems: 'center' }}>
          <SectionHead
            label="Why Inverio"
            title="One platform. Both sides of the table."
            sub="Startups get investor-ready. Investors find pre-vetted deals. AI does the heavy lifting."
          />

          <View style={{ maxWidth: 1200, width: '100%', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 24 : 32 }}>
            {/* Startup */}
            <Animated.View entering={FadeInDown.delay(100).duration(600)} style={{ flex: 1 }}>
              <View style={{ ...cardOnGray, borderRadius: 16, padding: isMobile ? 24 : 32 }}>
                <View style={{ backgroundColor: c.tealDim, borderRadius: 20, paddingHorizontal: 14, paddingVertical: 6, alignSelf: 'flex-start', marginBottom: 16 }}>
                  <Text style={{ color: c.teal, fontSize: 13, fontFamily: f(500) }}>For Startups</Text>
                </View>
                <Text style={{ color: c.white, fontSize: isMobile ? 22 : 26, fontFamily: f(700), marginBottom: 20 }}>
                  Raise with confidence, not guesswork.
                </Text>
                {[
                  { icon: Zap, title: 'Instant readiness score', desc: '15+ criteria scored by AI. Know where you stand in minutes.' },
                  { icon: FileSearch, title: 'Smart data room', desc: 'Upload docs. AI finds gaps before investors do.' },
                  { icon: Users, title: 'Investor matching', desc: 'Matched by thesis fit. No more spray and pray.' },
                  { icon: TrendingUp, title: 'Valuation benchmark', desc: '4 methods. Calibrated for Swiss early-stage.' },
                ].map((item, i) => (
                  <View key={i} style={{ flexDirection: 'row', gap: 14, marginBottom: i < 3 ? 18 : 0 }}>
                    <View style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: c.tealDim, alignItems: 'center', justifyContent: 'center' }}>
                      <item.icon size={18} color={c.teal} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ color: c.white, fontSize: 15, fontFamily: f(500), marginBottom: 2 }}>{item.title}</Text>
                      <Text style={{ color: c.text2, fontSize: 13, fontFamily: f(400), lineHeight: 19 }}>{item.desc}</Text>
                    </View>
                  </View>
                ))}
              </View>
            </Animated.View>

            {/* Investor */}
            <Animated.View entering={FadeInDown.delay(250).duration(600)} style={{ flex: 1 }}>
              <View style={{ ...cardOnGray, borderRadius: 16, padding: isMobile ? 24 : 32 }}>
                <View style={{ backgroundColor: c.indigoDim, borderRadius: 20, paddingHorizontal: 14, paddingVertical: 6, alignSelf: 'flex-start', marginBottom: 16 }}>
                  <Text style={{ color: c.indigo, fontSize: 13, fontFamily: f(500) }}>For Investors</Text>
                </View>
                <Text style={{ color: c.white, fontSize: isMobile ? 22 : 26, fontFamily: f(700), marginBottom: 20 }}>
                  Better deals. Less noise. Faster decisions.
                </Text>
                {[
                  { icon: BarChart3, title: 'Pre-scored pipeline', desc: 'Every startup arrives with an AI readiness breakdown.' },
                  { icon: Target, title: 'AI-assisted DD', desc: 'Ask anything about a data room. Get cited answers.' },
                  { icon: Inbox, title: 'Submission dropbox', desc: 'Share a link. Receive scored decks automatically.' },
                  { icon: LineChart, title: 'Portfolio intelligence', desc: 'Track readiness trajectories across your book.' },
                ].map((item, i) => (
                  <View key={i} style={{ flexDirection: 'row', gap: 14, marginBottom: i < 3 ? 18 : 0 }}>
                    <View style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: c.indigoDim, alignItems: 'center', justifyContent: 'center' }}>
                      <item.icon size={18} color={c.indigo} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ color: c.white, fontSize: 15, fontFamily: f(500), marginBottom: 2 }}>{item.title}</Text>
                      <Text style={{ color: c.text2, fontSize: 13, fontFamily: f(400), lineHeight: 19 }}>{item.desc}</Text>
                    </View>
                  </View>
                ))}
              </View>
            </Animated.View>
          </View>
        </View>

        {/* ══════════════════════════════════════════════════════════════════
            HOW IT WORKS — primary bg (darker)
        ══════════════════════════════════════════════════════════════════ */}
        <View onLayout={(e) => { howItWorksY.current = e.nativeEvent.layout.y; }} style={{ paddingHorizontal: px, paddingVertical: isMobile ? 60 : 100, backgroundColor: c.bgPrimary, alignItems: 'center' }}>
          <SectionHead
            label="How it works"
            title="Two paths. One platform."
            sub="Whether you're raising or deploying — Inverio gets you there faster."
          />

          <View style={{ maxWidth: 1100, width: '100%', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 24 : 32 }}>
            {/* Startup path */}
            <Animated.View entering={FadeInUp.delay(200).duration(600)} style={{ flex: 1 }}>
              <View style={{ ...cardOnWhite, borderRadius: 16, padding: isMobile ? 24 : 32, height: '100%' }}>
                <View style={{ backgroundColor: c.tealDim, borderRadius: 20, paddingHorizontal: 14, paddingVertical: 6, alignSelf: 'flex-start', marginBottom: 16 }}>
                  <Text style={{ color: c.teal, fontSize: 13, fontFamily: f(500) }}>Startup Journey</Text>
                </View>
                <Text style={{ color: c.white, fontSize: isMobile ? 22 : 26, fontFamily: f(700), marginBottom: 8, minHeight: isMobile ? undefined : 68 }}>
                  Raise with clarity.
                </Text>
                <Text style={{ color: c.text2, fontSize: 14, fontFamily: f(400), lineHeight: 22, marginBottom: 24, minHeight: isMobile ? undefined : 44 }}>
                  Stop guessing what investors want. Know your score, fix your gaps, get matched.
                </Text>

                {[
                  { num: '01', title: 'Upload your deck', desc: '2 minutes. We handle the rest.' },
                  { num: '02', title: 'Get your AI score', desc: '15+ criteria. Instant. No bias.' },
                  { num: '03', title: 'Fix what matters', desc: 'Prioritized actions ranked by fundraise impact.' },
                  { num: '04', title: 'Get discovered', desc: 'Thesis-fit investors find you. No cold outreach.' },
                ].map((step, i, arr) => (
                  <View key={i} style={{ flexDirection: 'row', gap: 14, marginBottom: i < arr.length - 1 ? 4 : 0, alignItems: 'flex-start' }}>
                    <View style={{ alignItems: 'center', width: 32 }}>
                      <View style={{
                        width: 32, height: 32, borderRadius: 16, backgroundColor: c.tealDim,
                        borderWidth: 2, borderColor: c.teal, alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Text style={{ color: c.teal, fontSize: 12, fontFamily: f(700) }}>{step.num}</Text>
                      </View>
                      {i < arr.length - 1 && (
                        <View style={{ width: 2, height: 28, backgroundColor: c.teal, opacity: 0.25, marginVertical: 4 }} />
                      )}
                    </View>
                    <View style={{ flex: 1, paddingTop: 4 }}>
                      <Text style={{ color: c.white, fontSize: 15, fontFamily: f(500), marginBottom: 2 }}>{step.title}</Text>
                      <Text style={{ color: c.text2, fontSize: 13, fontFamily: f(400), lineHeight: 19 }}>{step.desc}</Text>
                    </View>
                  </View>
                ))}

                <View style={{ marginTop: 'auto' as any, paddingTop: 24 }}>
                  <Pressable onPress={scrollToWaitlist} style={{
                    backgroundColor: c.teal, borderRadius: 10, paddingVertical: 14, alignItems: 'center',
                  }}>
                    <Text style={{ color: c.ctaTextOnAccent, fontFamily: f(700), fontSize: 15 }}>Get Scored — It's Free</Text>
                  </Pressable>
                </View>
              </View>
            </Animated.View>

            {/* Investor path */}
            <Animated.View entering={FadeInUp.delay(350).duration(600)} style={{ flex: 1 }}>
              <View style={{ ...cardOnWhite, borderRadius: 16, padding: isMobile ? 24 : 32, height: '100%' }}>
                <View style={{ backgroundColor: c.indigoDim, borderRadius: 20, paddingHorizontal: 14, paddingVertical: 6, alignSelf: 'flex-start', marginBottom: 16 }}>
                  <Text style={{ color: c.indigo, fontSize: 13, fontFamily: f(500) }}>Investor Journey</Text>
                </View>
                <Text style={{ color: c.white, fontSize: isMobile ? 22 : 26, fontFamily: f(700), marginBottom: 8, minHeight: isMobile ? undefined : 68 }}>
                  Deploy capital with conviction.
                </Text>
                <Text style={{ color: c.text2, fontSize: 14, fontFamily: f(400), lineHeight: 22, marginBottom: 24, minHeight: isMobile ? undefined : 44 }}>
                  Pre-scored deals. AI-assisted DD. Skip the noise, commit faster.
                </Text>

                {[
                  { num: '01', title: 'Set your thesis', desc: 'Stage, sector, geography. We only show what fits.' },
                  { num: '02', title: 'Browse scored deals', desc: 'Every startup has an AI readiness breakdown.' },
                  { num: '03', title: 'Accelerate DD', desc: 'AI analyses data rooms. Flags risks. Saves weeks.' },
                  { num: '04', title: 'Skip or commit', desc: 'Structured data means faster pass/invest decisions.' },
                ].map((step, i, arr) => (
                  <View key={i} style={{ flexDirection: 'row', gap: 14, marginBottom: i < arr.length - 1 ? 4 : 0, alignItems: 'flex-start' }}>
                    <View style={{ alignItems: 'center', width: 32 }}>
                      <View style={{
                        width: 32, height: 32, borderRadius: 16, backgroundColor: c.indigoDim,
                        borderWidth: 2, borderColor: c.indigo, alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Text style={{ color: c.indigo, fontSize: 12, fontFamily: f(700) }}>{step.num}</Text>
                      </View>
                      {i < arr.length - 1 && (
                        <View style={{ width: 2, height: 28, backgroundColor: c.indigo, opacity: 0.25, marginVertical: 4 }} />
                      )}
                    </View>
                    <View style={{ flex: 1, paddingTop: 4 }}>
                      <Text style={{ color: c.white, fontSize: 15, fontFamily: f(500), marginBottom: 2 }}>{step.title}</Text>
                      <Text style={{ color: c.text2, fontSize: 13, fontFamily: f(400), lineHeight: 19 }}>{step.desc}</Text>
                    </View>
                  </View>
                ))}

                <View style={{ marginTop: 'auto' as any, paddingTop: 24 }}>
                  <Pressable onPress={scrollToWaitlist} style={{
                    backgroundColor: c.indigo, borderRadius: 10, paddingVertical: 14, alignItems: 'center',
                  }}>
                    <Text style={{ color: '#FFFFFF', fontFamily: f(700), fontSize: 15 }}>Browse Deals — Join Beta</Text>
                  </Pressable>
                </View>
              </View>
            </Animated.View>
          </View>
        </View>

        {/* ══════════════════════════════════════════════════════════════════
            TRUST / ABOUT — elevated bg
        ══════════════════════════════════════════════════════════════════ */}
        <View onLayout={(e) => { aboutY.current = e.nativeEvent.layout.y; }} style={{ paddingHorizontal: px, paddingVertical: isMobile ? 60 : 80, backgroundColor: c.bgElevated, alignItems: 'center' }}>
          <SectionHead
            label="About"
            title="Built in Switzerland. Built for trust."
          />

          <Animated.View entering={FadeInUp.delay(50).duration(500)} style={{ marginTop: isMobile ? -28 : -40, marginBottom: isMobile ? 28 : 40, alignItems: 'center' }}>
            <Text style={{ color: c.text2, fontSize: isMobile ? 15 : 17, fontFamily: f(400), textAlign: 'center', lineHeight: isMobile ? 22 : 26, fontStyle: 'italic' }}>
              Built by angels, for founders. Built by a founder, for angels.
            </Text>
          </Animated.View>

          <View style={{ maxWidth: 900, width: '100%', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 16 : 24, flexWrap: 'wrap' }}>
            {[
              { icon: Target, title: 'Transparent scoring', desc: 'Every score comes with a full breakdown. No black boxes.' },
              { icon: Zap, title: 'AI-powered, human-centered', desc: 'AI accelerates analysis. You make the decisions.' },
              { icon: FileSearch, title: 'Encrypted, EU-hosted', desc: 'Encrypted in transit and at rest, stored in Frankfurt. You can delete your documents at any time.' },
              { icon: Users, title: 'Two-sided by design', desc: 'Startups and investors on one platform. Network effects built in.' },
            ].map((item, i) => (
              <Animated.View key={i} entering={FadeInUp.delay(100 + i * 100).duration(500)} style={{ width: isMobile ? '100%' : '47%' as any }}>
                <View style={{ flexDirection: 'row', gap: 14, padding: 20, ...cardOnGray, borderRadius: 14 }}>
                  <View style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: c.tealDim, alignItems: 'center', justifyContent: 'center' }}>
                    <item.icon size={20} color={c.teal} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ color: c.white, fontSize: 15, fontFamily: f(500), marginBottom: 4 }}>{item.title}</Text>
                    <Text style={{ color: c.text2, fontSize: 13, fontFamily: f(400), lineHeight: 19 }}>{item.desc}</Text>
                  </View>
                </View>
              </Animated.View>
            ))}
          </View>
        </View>

        {/* ══════════════════════════════════════════════════════════════════
            WAITLIST CTA — primary bg
        ══════════════════════════════════════════════════════════════════ */}
        <View
          onLayout={onWaitlistLayout}
          style={{ paddingHorizontal: px, paddingVertical: isMobile ? 60 : 100, backgroundColor: c.bgPrimary, alignItems: 'center' }}
        >
          <Animated.View entering={FadeInUp.delay(100).duration(600)} style={{ maxWidth: 600, width: '100%', alignItems: 'center', gap: 16 }}>
            <Text style={{ color: c.white, fontSize: isMobile ? 28 : 40, fontFamily: f(700), textAlign: 'center' }}>
              Ready to move faster?
            </Text>
            <Text style={{ color: c.text2, fontSize: 16, fontFamily: f(400), textAlign: 'center', lineHeight: 24 }}>
              Limited beta spots. Refer friends to skip ahead.
            </Text>

            <WaitlistForm colorScheme={mode} />

            {showCount && waitlistCount !== null && (
              <Animated.View entering={FadeIn.duration(500)}>
                <Text style={{ color: c.text3, fontSize: 14, fontFamily: f(400), marginTop: 16 }}>
                  {waitlistCount.toLocaleString()}+ founders and investors already in line
                </Text>
              </Animated.View>
            )}
          </Animated.View>
        </View>

        {/* ══════════════════════════════════════════════════════════════════
            FOOTER — deepest bg
        ══════════════════════════════════════════════════════════════════ */}
        {/* paddingBottom clears the floating BetaBubbleButton so it never covers the last footer links */}
        <View style={{ paddingHorizontal: px, paddingTop: 24, paddingBottom: 104, borderTopWidth: 1, borderTopColor: c.border, backgroundColor: c.bgDeep }}>
          <View style={{
            maxWidth: 1200, width: '100%', alignSelf: 'center',
            flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'center', gap: isMobile ? 16 : 0,
          }}>
            <Text style={{ color: c.text3, fontSize: 13, fontFamily: f(400) }} numberOfLines={1}>
              © 2026 Inverio · Geneva, Switzerland
            </Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', flexShrink: 1, rowGap: 8, columnGap: 24, justifyContent: isMobile ? 'center' : 'flex-end' }}>
              {[
                { label: 'About', path: '/about' },
                { label: 'Contact', path: '/contact' },
                { label: 'Help', path: '/help' },
                { label: 'Terms', path: '/terms' },
                { label: 'Privacy', path: '/privacy' },
                { label: 'Cookies', path: '/cookies' },
                { label: 'Refunds', path: '/refunds' },
                { label: 'Disclaimer', path: '/disclaimer' },
                { label: 'AI Disclosure', path: '/ai-disclosure' },
              ].map((link) => (
                <Pressable key={link.label} onPress={() => router.push(link.path as any)}>
                  <Text style={{ color: c.text2, fontSize: 13, fontFamily: f(400) }}>{link.label}</Text>
                </Pressable>
              ))}
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Beta floating button + modals */}
      <BetaBubbleButton colorScheme={mode} />
      <BetaLoginModal visible={showLoginModal} onClose={() => setShowLoginModal(false)} colorScheme={mode} />
      <WaitlistModal visible={showWaitlistModal} onClose={() => setShowWaitlistModal(false)} colorScheme={mode} />

      {/* Verification result overlay */}
      {verifyBanner && (
        <View style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.75)',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 100,
        }}>
          <Animated.View entering={FadeIn.duration(400)} style={{
            backgroundColor: isDark ? '#111827' : '#ffffff',
            borderRadius: 20,
            padding: 36,
            maxWidth: 420,
            width: '90%',
            alignItems: 'center',
            gap: 16,
            borderWidth: 1,
            borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
          }}>
            <View style={{
              width: 56, height: 56, borderRadius: 28,
              backgroundColor: verifyBanner === 'invalid' ? 'rgba(248,113,113,0.15)' : 'rgba(79,209,197,0.15)',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <Text style={{ fontSize: 28 }}>
                {verifyBanner === 'invalid' ? '✕' : '✓'}
              </Text>
            </View>
            <Text style={{
              color: verifyBanner === 'invalid' ? '#f87171' : '#4fd1c5',
              fontSize: 22,
              fontWeight: '700',
              fontFamily: f(700),
              textAlign: 'center',
            }}>
              {verifyBanner === 'success' ? 'Email Verified!' :
               verifyBanner === 'already' ? 'Already Verified' :
               'Link Invalid'}
            </Text>
            <Text style={{
              color: isDark ? 'rgba(255,255,255,0.7)' : '#48484a',
              fontSize: 15,
              textAlign: 'center',
              lineHeight: 22,
            }}>
              {verifyBanner === 'success' ? 'Your spot on the waitlist is confirmed. Check your inbox for your position and referral link.' :
               verifyBanner === 'already' ? 'Your email was already verified. You\'re all set — check your inbox for your referral link.' :
               'This verification link is invalid or expired. Please sign up again.'}
            </Text>
            <Pressable
              onPress={() => setVerifyBanner(null)}
              style={{
                backgroundColor: verifyBanner === 'invalid' ? '#f87171' : '#0F766E',
                borderRadius: 10,
                paddingHorizontal: 32,
                paddingVertical: 12,
                marginTop: 4,
              }}
            >
              <Text style={{ color: '#ffffff', fontWeight: '700', fontSize: 15 }}>
                {verifyBanner === 'invalid' ? 'Sign Up Again' : 'Got It'}
              </Text>
            </Pressable>
          </Animated.View>
        </View>
      )}
    </View>
  );
}
